/**
 * AIOptimizerCard.jsx — KI-Optimierer (07/2026)
 * Analysiert Latenz/Fehler aller KI-Dienste, empfiehlt Modelle,
 * Auto-Pilot wechselt automatisch (abschaltbar), 1-Klick-Übernahme.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Bot, Zap, Loader2, Check, History, Gauge, ArrowRight, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtMs = (ms) => (ms == null ? '–' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);
const urgencyColor = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600' };

export const AIOptimizerCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [benchmarking, setBenchmarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-optimizer/status`, auth());
      setStatus(data);
    } catch (e) {
      console.error('Optimizer status failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSettings = async (patch) => {
    const next = { ...status.settings, ...patch };
    setStatus((s) => ({ ...s, settings: next }));
    try {
      await axios.put(`${API}/ai-optimizer/settings`, { auto_apply: next.auto_apply, weekly_enabled: next.weekly_enabled }, auth());
      toast.success(patch.auto_apply !== undefined
        ? (next.auto_apply ? 'Auto-Pilot aktiviert – Modelle werden automatisch optimiert' : 'Auto-Pilot aus – Wechsel nur noch per Knopfdruck')
        : (next.weekly_enabled ? 'Wöchentlicher Check aktiviert (Mo 07:30)' : 'Wöchentlicher Check deaktiviert'));
    } catch (e) {
      toast.error('Einstellung konnte nicht gespeichert werden');
      load();
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/ai-optimizer/analyze`, {}, auth());
      setStatus((s) => ({ ...s, last_run: data }));
      const applied = (data.recommendations || []).filter((r) => r.applied).length;
      const open = (data.recommendations || []).length - applied;
      if (applied) toast.success(`${applied} Modell-Wechsel automatisch durchgeführt`);
      else if (open) toast.info(`${open} Empfehlung(en) gefunden`);
      else toast.success('Alles optimal – kein Wechsel nötig ✅');
      load();
    } catch (e) {
      toast.error('Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const benchmark = async () => {
    setBenchmarking(true);
    try {
      const { data } = await axios.post(`${API}/ai-optimizer/benchmark`, {}, auth());
      setStatus((s) => ({ ...s, benchmark: data }));
      toast.success('Anbieter-Vergleich abgeschlossen');
    } catch (e) {
      toast.error('Vergleich fehlgeschlagen');
    } finally {
      setBenchmarking(false);
    }
  };

  const apply = async (rec) => {
    setApplying(rec.service_id);
    try {
      await axios.post(`${API}/ai-optimizer/apply`, {
        service_id: rec.service_id, provider: rec.provider, model: rec.model, reason: rec.reason,
      }, auth());
      toast.success(`${rec.label}: ${rec.model} übernommen`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally {
      setApplying(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-indigo-200" data-testid="ai-optimizer-card">
        <CardContent className="p-6 flex items-center gap-2 text-gray-500">
          <Loader2 className="animate-spin" size={18} /> KI-Optimierer lädt…
        </CardContent>
      </Card>
    );
  }

  const run = status?.last_run;
  const recs = run?.recommendations || [];
  const openRecs = recs.filter((r) => !r.applied);
  const snapshot = status?.snapshot?.services || [];
  const activeServices = snapshot.filter((s) => s.count_7d > 0);

  return (
    <Card className="border-2 border-indigo-300 bg-gradient-to-br from-indigo-50/60 to-white" data-testid="ai-optimizer-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="text-indigo-600" /> KI-Optimierer
          {status.settings.auto_apply && (
            <Badge className="bg-indigo-600 text-white" data-testid="ai-optimizer-autopilot-badge">Auto-Pilot aktiv</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Überwacht Antwortzeiten & Fehlerraten aller KI-Dienste und stellt automatisch das beste Modell ein.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-6 items-center p-3 bg-white rounded-lg border">
          <div className="flex items-center gap-2">
            <Switch checked={status.settings.auto_apply} onCheckedChange={(v) => updateSettings({ auto_apply: v })} data-testid="ai-optimizer-autopilot-switch" />
            <Label className="text-sm">Auto-Pilot (automatisch wechseln)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={status.settings.weekly_enabled} onCheckedChange={(v) => updateSettings({ weekly_enabled: v })} data-testid="ai-optimizer-weekly-switch" />
            <Label className="text-sm">Wöchentlicher Check (Mo 07:30 + E-Mail)</Label>
          </div>
          <Button onClick={analyze} disabled={analyzing} className="ml-auto bg-indigo-600 hover:bg-indigo-700" data-testid="ai-optimizer-analyze-btn">
            {analyzing ? <><Loader2 className="animate-spin mr-2" size={16} /> Analysiere…</> : <><Zap size={16} className="mr-2" /> System jetzt analysieren</>}
          </Button>
          <Button onClick={benchmark} disabled={benchmarking} variant="outline" data-testid="ai-optimizer-benchmark-btn">
            {benchmarking ? <><Loader2 className="animate-spin mr-2" size={16} /> Teste…</> : <><Trophy size={16} className="mr-2" /> Anbieter-Vergleich</>}
          </Button>
        </div>

        <div className="text-xs px-1 flex items-start gap-1" data-testid="ai-optimizer-failover-status">
          {(status?.snapshot?.available_providers?.length || 0) > 1 ? (
            <span className="text-green-700">
              🛡️ Ausfallsicherung aktiv: Fällt ein Anbieter aus, springt automatisch das Backup ein
              ({status.snapshot.available_providers.map((p) => p === 'openai' ? 'OpenAI' : p === 'anthropic' ? 'Claude' : 'Gemini').join(' → ')}).
              {(status?.failover_events?.length || 0) > 0 && ` ${status.failover_events.length} Failover-Einsatz/Einsätze protokolliert.`}
            </span>
          ) : (
            <span className="text-amber-700">
              🛡️ Ausfallsicherung bereit: Hinterlege im Tab „KI-Provider" einen Claude- oder Gemini-Key –
              dann übernimmt bei einem OpenAI-Ausfall automatisch das Backup (Kosten nur im Notfall).
            </span>
          )}
        </div>

        {run && (
          <div className="p-3 bg-white rounded-lg border space-y-3" data-testid="ai-optimizer-last-run">
            <div className="text-xs text-gray-500">
              Letzte Analyse: {new Date(run.created_at).toLocaleString('de-CH')} ({run.trigger === 'weekly' ? 'automatisch' : 'manuell'})
            </div>
            {run.model_radar?.checked && (
              <div className="text-xs text-indigo-600 flex items-center gap-1" data-testid="ai-optimizer-radar">
                🛰️ Modell-Radar: {run.model_radar.new_models?.length
                  ? `Neu bei OpenAI entdeckt: ${run.model_radar.new_models.join(', ')}`
                  : 'keine neuen OpenAI-Modelle – du bist auf dem neusten Stand'}
              </div>
            )}
            {run.summary && <p className="text-sm text-gray-700">{run.summary}</p>}
            {recs.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-green-700" data-testid="ai-optimizer-all-good">
                <Check size={16} /> Alle Dienste laufen mit dem optimalen Modell.
              </div>
            )}
            {recs.map((rec) => (
              <div key={rec.service_id} className="flex flex-wrap items-center gap-2 p-2 rounded border bg-gray-50" data-testid={`ai-optimizer-rec-${rec.service_id}`}>
                <Badge className={urgencyColor[rec.urgency] || urgencyColor.low}>{rec.urgency}</Badge>
                <span className="text-sm font-medium">{rec.label}</span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {rec.current_model} <ArrowRight size={12} /> <strong className="text-indigo-700">{rec.model}</strong>
                </span>
                <span className="text-xs text-gray-500 w-full sm:w-auto sm:flex-1">{rec.reason}</span>
                {rec.applied ? (
                  <Badge className="bg-green-100 text-green-700" data-testid={`ai-optimizer-applied-${rec.service_id}`}><Check size={12} className="mr-1" /> Übernommen</Badge>
                ) : (
                  <Button size="sm" variant="outline" disabled={applying === rec.service_id} onClick={() => apply(rec)} data-testid={`ai-optimizer-apply-${rec.service_id}`}>
                    {applying === rec.service_id ? <Loader2 className="animate-spin" size={14} /> : 'Übernehmen'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {activeServices.length > 0 && (
          <div className="p-3 bg-white rounded-lg border" data-testid="ai-optimizer-health">
            <div className="flex items-center gap-2 text-sm font-medium mb-2"><Gauge size={16} className="text-indigo-600" /> Performance (7 Tage)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {activeServices.map((s) => (
                <div key={s.service_id} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-700">{s.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-gray-400">{s.model}</span>
                    <span className={s.p90_ms > 8000 && s.kundennah ? 'text-red-600 font-medium' : 'text-gray-600'}>P90 {fmtMs(s.p90_ms)}</span>
                    <span className={s.success_rate < 95 ? 'text-red-600 font-medium' : 'text-green-600'}>{s.success_rate}%</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {status?.benchmark && (
          <div className="p-3 bg-white rounded-lg border" data-testid="ai-optimizer-benchmark">
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              <Trophy size={16} className="text-amber-500" /> Anbieter-Vergleich (echte Kundenfragen)
              <span className="text-xs text-gray-400 font-normal ml-auto">{new Date(status.benchmark.created_at).toLocaleString('de-CH')}</span>
            </div>
            <div className="space-y-1">
              {(status.benchmark.results || []).map((r, i) => {
                const isWinner = status.benchmark.fastest_quality?.model === r.model || status.benchmark.fastest_fast?.model === r.model;
                return (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                    <span className={isWinner ? 'font-semibold text-amber-700' : 'text-gray-700'}>
                      {isWinner && '🏆 '}{r.provider === 'openai' ? 'OpenAI' : r.provider === 'anthropic' ? 'Claude' : 'Gemini'} · {r.model}
                      <Badge variant="outline" className="ml-2 text-[10px]">{r.tier === 'quality' ? 'Qualität' : 'Schnell'}</Badge>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-gray-600">Ø {fmtMs(r.avg_ms)}</span>
                      <span className={r.failed ? 'text-red-600' : 'text-green-600'}>{r.success}/{r.success + r.failed}</span>
                    </span>
                  </div>
                );
              })}
            </div>
            {(status.benchmark.providers || []).length < 2 && (
              <p className="text-[11px] text-amber-700 mt-2">Nur OpenAI getestet – hinterlege einen Claude/Gemini-Key für den echten Anbieter-Vergleich (läuft dann automatisch).</p>
            )}
          </div>
        )}

        {(status?.history || []).length > 0 && (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="ai-optimizer-history-toggle">
              <History size={14} className="mr-1" /> Wechsel-Historie ({status.history.length})
            </Button>
            {showHistory && (
              <div className="mt-2 space-y-1" data-testid="ai-optimizer-history">
                {status.history.map((h) => (
                  <div key={h.id} className="text-xs text-gray-600 flex flex-wrap items-center gap-1 p-1.5 bg-gray-50 rounded">
                    <span className="text-gray-400">{new Date(h.created_at).toLocaleString('de-CH')}</span>
                    <span className="font-medium">{h.label}:</span>
                    <span>{h.from_model}</span><ArrowRight size={10} /><span className="font-medium">{h.to_model}</span>
                    <Badge variant="outline" className="text-[10px]">{h.source.startsWith('auto') ? 'automatisch' : 'manuell'}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIOptimizerCard;
